/**
 * The storage half of the file-transfer platform seam.
 *
 * `receiverWorkerClient` already injects its platform-specific piece — the `Worker`
 * factory — and its docblock describes the module as "platform-clean… deps-injection
 * over platform branching". That was true of `Worker` and not true of storage: both it
 * and `opfsSenderSource` still reached `navigator.storage` directly. This finishes the
 * sentence the existing seam started (spec R2-b); it is not a new abstraction.
 *
 * WHY THIS IS WORTH A MODULE, AND WHY THE CHECK IS A PROPERTY CHECK. `client-core` has
 * no `node:` imports and is otherwise Node-safe, so `client-node` and the Electron main
 * process are about to depend on the barrel. These two modules are already IMPORTABLE
 * under Node — every `navigator` access sits inside a function body — so the failure
 * only appears at CALL time. And the obvious guard does not work:
 *
 *     if (typeof navigator !== "undefined") { navigator.storage.getDirectory(); }
 *
 * Node 24 DEFINES a `navigator` global. That guard passes, `navigator.storage` is
 * `undefined`, and the result is
 * `TypeError: Cannot read properties of undefined (reading 'getDirectory')` — an opaque
 * crash produced by a check that looked defensive. So `detectOpfsStorage` probes the
 * PROPERTY it actually needs rather than the global's existence (spec R2-a).
 *
 * WHAT A CALLER GETS INSTEAD. A `StorageUnsupportedError` naming the missing capability
 * (spec R2-d). For a package about to become the foundation of a Node host, a
 * `TypeError` from deep inside a stream adapter is not an acceptable answer to "this
 * platform has no OPFS".
 *
 * NOT A ROUTE TO FILE TRANSFER ON THE DESKTOP. The desktop app uses `client-node`'s
 * `nodeSenderSource` / `nodeReceiverOutput`. This exists so the `client-core` barrel is
 * honestly Node-importable at RUN time — nothing more (spec R2-e).
 */

/**
 * The slice of `StorageManager` the file-transfer layer uses.
 *
 * Deliberately narrow: two methods, both already used. A wider interface would invite
 * new `navigator.storage` reaches to be "already covered" without being injected.
 */
export interface OpfsStorage {
  getDirectory(): Promise<FileSystemDirectoryHandle>;
  estimate(): Promise<{ quota?: number; usage?: number }>;
}

/**
 * Thrown when an OPFS-backed entry point is reached on a platform that has none.
 *
 * Carries a stable `code` so callers can branch without matching on message text.
 */
export class StorageUnsupportedError extends Error {
  readonly code = "storage_unsupported";

  constructor(operation: string) {
    super(
      `Origin-private file system is unavailable on this platform (needed for: ${operation}). ` +
        "This code path requires a browser-class environment; Node hosts should use " +
        "@reflecto/client-node's file adapters instead."
    );
    this.name = "StorageUnsupportedError";
  }
}

/**
 * The platform's OPFS storage, or `null` where there is none.
 *
 * Probes `navigator.storage.getDirectory` as a property — see the module docblock for
 * why `typeof navigator` is the wrong test.
 */
export function detectOpfsStorage(): OpfsStorage | null {
  const storage = (globalThis as { navigator?: { storage?: unknown } }).navigator?.storage as
    | (StorageManager & Partial<OpfsStorage>)
    | undefined;
  if (typeof storage?.getDirectory !== "function") return null;
  return storage as unknown as OpfsStorage;
}

/**
 * Unwrap injected storage, or throw the clean unsupported signal.
 *
 * `operation` names what needed it, so the message points at the call rather than the
 * capability in the abstract.
 */
export function requireOpfsStorage(
  storage: OpfsStorage | null | undefined,
  operation: string
): OpfsStorage {
  if (!storage) throw new StorageUnsupportedError(operation);
  return storage;
}

/** Storage dependency carried by the OPFS-backed factories. */
export interface OpfsStorageDeps {
  /**
   * Platform storage. Hosts normally pass `detectOpfsStorage()` directly; `null` is
   * accepted so a caller need not branch, and surfaces as `StorageUnsupportedError`
   * at the point of use rather than a `TypeError` later.
   */
  storage: OpfsStorage | null;
}
